import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Package, Heart, User, LogOut } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { useLang } from "../i18n";
import ProductCard from "../components/ProductCard";

const STATUS_COLORS = {
  in_attesa_pagamento: "bg-gray-100 text-gray-700",
  nuovo: "bg-amber-100 text-amber-900",
  in_lavorazione: "bg-blue-100 text-blue-900",
  spedito: "bg-purple-100 text-purple-900",
  consegnato: "bg-green-100 text-green-900",
  annullato: "bg-red-100 text-red-900",
};

export default function Account() {
  const { t, lang } = useLang();
  const { user, logout } = useAuth();
  const { wishlist } = useCart();
  const navigate = useNavigate();
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    if (user === null) navigate("/accedi?redirect=/account");
  }, [user, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/orders/my").then((r) => setOrders(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, [user]);

  if (!user) return null;
  const wishedProducts = products.filter((p) => wishlist.includes(p.id));

  const tabs = [
    { id: "orders", label: t("account.orders"), icon: Package },
    { id: "wishlist", label: t("account.wishlist"), icon: Heart },
    { id: "profile", label: t("account.profile"), icon: User },
  ];

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14" data-testid="account-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-4xl font-bold tracking-tight" data-testid="account-title">{t("account.title")}</h1>
          <p className="mt-2 text-cocoa/60">{user.name} · {user.email}</p>
        </div>
        <button onClick={async () => { await logout(); navigate("/"); }} data-testid="logout-btn"
          className="flex items-center gap-2 border border-[#E5D9C8] px-5 py-2.5 text-sm text-cocoa/70 hover:border-terra hover:text-terra transition-colors">
          <LogOut size={15} /> {t("nav.logout")}
        </button>
      </div>
      <div className="mt-10 flex gap-2 border-b border-[#E5D9C8]" data-testid="account-tabs">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`account-tab-${id}`}
            className={`flex items-center gap-2 px-5 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === id ? "border-honey text-honey" : "border-transparent text-cocoa/60 hover:text-honey"}`}>
            <Icon size={15} /> {label}
          </button>
        ))}
      </div>

      {tab === "orders" && (
        <div className="mt-8 space-y-4" data-testid="orders-list">
          {orders.length === 0 && (
            <div className="py-16 text-center" data-testid="orders-empty">
              <p className="text-cocoa/60">{t("account.no.orders")}</p>
              <Link to="/shop" className="mt-4 inline-block text-honey font-medium underline underline-offset-4">{t("cart.empty.cta")}</Link>
            </div>
          )}
          {orders.map((o, i) => (
            <motion.div key={o.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="bg-white border border-[#E5D9C8] rounded-sm p-6" data-testid={`order-card-${o.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-mono text-xs text-cocoa/50">#{o.id.slice(0, 8)} · {new Date(o.created_at).toLocaleDateString(lang === "it" ? "it-IT" : "en-GB")}</p>
                  <p className="mt-1 font-serif text-lg font-semibold">{o.items.map((it) => `${lang === "it" ? it.name_it : it.name_en} (${it.size}) ×${it.qty}`).join(", ")}</p>
                </div>
                <div className="text-right">
                  <span className={`inline-block px-3 py-1 text-xs font-mono rounded-full ${STATUS_COLORS[o.status] || STATUS_COLORS.nuovo}`} data-testid={`order-status-${o.id}`}>
                    {t(`order.status.${o.status}`)}
                  </span>
                  <p className="mt-1.5 font-mono font-semibold">€{o.total.toFixed(2)}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {tab === "wishlist" && (
        <div className="mt-8" data-testid="wishlist-section">
          {wishedProducts.length === 0 ? (
            <p className="py-16 text-center text-cocoa/60" data-testid="wishlist-empty">{t("account.wishlist.empty")}</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {wishedProducts.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          )}
        </div>
      )}

      {tab === "profile" && (
        <div className="mt-8 max-w-md bg-white border border-[#E5D9C8] rounded-sm p-7 space-y-4" data-testid="profile-section">
          <div><p className="font-mono text-[10px] uppercase tracking-widest text-cocoa/50">{t("auth.name")}</p><p className="mt-1 font-medium">{user.name}</p></div>
          <div><p className="font-mono text-[10px] uppercase tracking-widest text-cocoa/50">{t("auth.email")}</p><p className="mt-1 font-medium">{user.email}</p></div>
        </div>
      )}
    </div>
  );
}
